import { useListAgentsQuery } from '@/store/features/gtm-agents.feature';
import type { AgentDto } from '@/types/gtm-agents';
import { useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';
import { pickSelectedHref } from '../nav-match.util';
import {
  useLockedUpgradeClick,
  type LockedUpgradeClick,
} from '../use-locked-upgrade-click.hook';
import {
  useSidebarNavAccess,
  type SidebarNavAccess,
} from '../use-sidebar-nav-access.hook';
import { workspaceAgentId, type ChatTab } from './chat-tabs.util';
import {
  coveredPaths,
  destinationState,
  isPinnablePage,
  isSettingsPath,
  pinnableDestinations,
  SIDEBAR_DESTINATIONS,
  type SidebarDestination,
} from './sidebar-destinations';
import {
  defaultLayout,
  missingPages,
  pinnedChatIds,
  refKey,
  type SidebarLayout,
  type SidebarRef,
} from './sidebar-layout.util';
import { useChatTabs, type ChatTabsState } from './use-chat-tabs.hook';
import { usePublishAgentMarkOrder } from '@/containers/agents/agent-mark-order.store';
import { useFavoriteAgentPins } from './use-favorite-agent-pins.hook';
import { useRetryOnError } from './use-retry-on-error.hook';
import {
  useSidebarLayout,
  type SidebarLayoutState,
} from './use-sidebar-layout.hook';

/** A pinned tile or row, as the sidebar draws it. */
export interface TabsNavItem {
  /** The ref as one string, unique in the sidebar. */
  key: string;
  ref: SidebarRef;
  label: string;
  /** A DynamicIcon name; unused on an agent's or a chat's pin. */
  iconName: string;
  /** Where the pin goes; a chat's pin opens its chat instead. */
  href?: string;
  /** Match only this path, not its detail routes. */
  exact?: boolean;
  /** Other paths the pin covers. */
  also?: readonly string[];
  locked?: boolean;
  lockedTooltip?: string;
  /** Set on an agent's own pin. */
  agentId?: string;
  /** Set on a chat's pin. */
  chat?: ChatTab;
}

type NavAgent = Pick<AgentDto, 'id' | 'name' | 'archivedAt'>;

/**
 * A ref as the item it draws, or null when this user can't see it. Settings
 * draws nothing: the footer's gear goes there.
 */
function itemFor(
  ref: SidebarRef,
  access: SidebarNavAccess,
  agents: ReadonlyMap<string, NavAgent>,
  pinnedChats: ReadonlyMap<string, ChatTab>
): TabsNavItem | null {
  const key = refKey(ref);
  if (ref.kind === 'page') {
    const state = destinationState(ref.id, access);
    if (!state.visible || !isPinnablePage(ref.id)) return null;
    const page = SIDEBAR_DESTINATIONS[ref.id];
    return {
      key,
      ref,
      label: page.label,
      iconName: page.iconName,
      href: page.href,
      exact: page.exact,
      also: page.also,
      locked: state.locked || undefined,
      lockedTooltip: state.locked ? page.lockedTooltip : undefined,
    };
  }
  if (ref.kind === 'agent') {
    const agent = access.agentsUnlocked ? agents.get(ref.id) : undefined;
    if (!agent || agent.archivedAt) return null;
    return {
      key,
      ref,
      label: agent.name,
      iconName: 'robot',
      href: `/agents/${agent.id}`,
      agentId: agent.id,
    };
  }
  const chat = pinnedChats.get(ref.id);
  if (!chat) return null;
  return { key, ref, label: chat.title, iconName: 'comment', chat };
}

/**
 * The layout as tiles and rows, skipping what this user can no longer see: a
 * page they lost access to, an agent deleted or archived, a chat deleted.
 */
export function buildTabsNav(
  layout: SidebarLayout,
  access: SidebarNavAccess,
  agents: readonly NavAgent[],
  pinnedChats: ReadonlyMap<string, ChatTab> = new Map()
): { favorites: TabsNavItem[]; pinned: TabsNavItem[] } {
  const byId = new Map(agents.map((agent) => [agent.id, agent]));
  const items = (refs: SidebarRef[]) =>
    refs.flatMap((ref) => itemFor(ref, access, byId, pinnedChats) ?? []);
  return { favorites: items(layout.favorites), pinned: items(layout.pinned) };
}

/**
 * The pin the screen selects: the chat's when a pinned chat is on screen,
 * none when another chat is, else the most specific pin covering the path.
 */
export function selectedPinKey(
  pathname: string,
  items: readonly TabsNavItem[],
  activeChatId: string | null
): string | null {
  if (activeChatId)
    return items.find((item) => item.chat?.id === activeChatId)?.key ?? null;
  const paths = coveredPaths(items);
  const href = pickSelectedHref(pathname, paths);
  return paths.find((path) => path.href === href)?.key ?? null;
}

/**
 * The page on screen when nothing pinned covers it (the user unpinned it, or
 * opened it from the command palette): it shows as the first tab, with Pin.
 * Null when a pin covers it (`pagePinKey`), and on every Settings page, where
 * the footer's gear is selected instead. An agent's workspace is that agent,
 * not the Agents page, so an agent without a pin of its own is the tab even
 * while Agents is pinned, and Pin pins the agent.
 */
export function currentPageTab(
  pathname: string,
  access: SidebarNavAccess,
  pagePinKey: string | null,
  agents: ReadonlyMap<string, NavAgent> = new Map()
): TabsNavItem | null {
  const agentId = workspaceAgentId(pathname);
  if (agentId) {
    const ref: SidebarRef = { kind: 'agent', id: agentId };
    if (pagePinKey === refKey(ref)) return null;
    const agent = itemFor(ref, access, agents, new Map());
    if (agent) return agent;
  }
  if (pagePinKey) return null;
  const paths = coveredPaths(
    pinnableDestinations(access).map((page) => ({ ...page, key: page.id }))
  );
  const href = pickSelectedHref(pathname, paths);
  const id = paths.find((path) => path.href === href)?.key;
  return id
    ? itemFor({ kind: 'page', id }, access, new Map(), new Map())
    : null;
}

export interface TabsNav {
  favorites: TabsNavItem[];
  pinned: TabsNavItem[];
  /** The tile or row for what is on screen. */
  selectedKey: string | null;
  /** The page on screen when no pin covers it, as a tab. */
  pageTab: TabsNavItem | null;
  /** The page tab is what is on screen, with no chat in front of it. */
  pageTabActive: boolean;
  /**
   * A Settings page is on screen, with no chat in front of it: the footer's
   * gear is selected.
   */
  settingsActive: boolean;
  /** The pages this user can see that the layout leaves out. */
  missingPages: SidebarDestination[];
  layout: SidebarLayoutState;
  chats: ChatTabsState;
  /** Which pages this user sees, and which carry the upgrade lock. */
  access: SidebarNavAccess;
  /** What a click on a locked page does; see `useLockedUpgradeClick`. */
  onLockedClick?: LockedUpgradeClick;
}

/**
 * The sidebar's pins and chats: the user's layout drawn with the sidebar's
 * access rules for each entry, the one the screen selects, and
 * what a click on a locked page does. A star on an agent pins it, as
 * favorite agents always showed here.
 */
export function useTabsNav(): TabsNav {
  const access = useSidebarNavAccess();
  const onLockedClick = useLockedUpgradeClick();
  const {
    data: agents,
    isError: agentsFailed,
    isSuccess: agentsRead,
    refetch: refetchAgents,
  } = useListAgentsQuery(undefined, {
    skip: !access.agentsUnlocked,
    refetchOnReconnect: true,
  });
  useRetryOnError(
    { isError: agentsFailed, isSuccess: agentsRead },
    refetchAgents
  );
  usePublishAgentMarkOrder(agents);
  const defaults = useMemo(() => defaultLayout(agents ?? []), [agents]);
  // The defaults pin the favorite agents, so they wait for the agents.
  const layout = useSidebarLayout(defaults, {
    defaultsReady: !access.agentsUnlocked || agents !== undefined,
  });
  useFavoriteAgentPins(agents, layout);
  const chatIds = useMemo(() => pinnedChatIds(layout.layout), [layout.layout]);
  const chats = useChatTabs(chatIds);
  const pathname = useLocation({ select: (location) => location.pathname });
  const nav = useMemo(
    () => buildTabsNav(layout.layout, access, agents ?? [], chats.pinned),
    [access, agents, chats.pinned, layout.layout]
  );
  const agentsById = useMemo(
    () => new Map((agents ?? []).map((agent) => [agent.id, agent])),
    [agents]
  );
  const all = useMemo(() => [...nav.favorites, ...nav.pinned], [nav]);
  // The pin covering the page, whether or not a chat is in front of it.
  const pagePinKey = useMemo(
    () => selectedPinKey(pathname, all, null),
    [all, pathname]
  );
  // A chat is the page on /clu and in an agent's workspace. In the Clu
  // dock it sits beside the page, which keeps its tab.
  const chatIsPage = chats.activeId !== null && !chats.activeInDock;
  const pageTab = useMemo(
    () =>
      chatIsPage
        ? null
        : currentPageTab(pathname, access, pagePinKey, agentsById),
    [access, agentsById, chatIsPage, pagePinKey, pathname]
  );
  // One item is selected: the chat on screen, else the page's pin.
  const selectedKey = useMemo(
    () =>
      chats.activeId
        ? selectedPinKey(pathname, all, chats.activeId)
        : pageTab
          ? null
          : pagePinKey,
    [all, chats.activeId, pagePinKey, pageTab, pathname]
  );
  const pageTabActive = pageTab !== null && chats.activeId === null;
  const settingsActive = chats.activeId === null && isSettingsPath(pathname);
  const missing = useMemo(
    () => missingPages(layout.layout, access),
    [access, layout.layout]
  );
  return {
    ...nav,
    selectedKey,
    pageTab,
    pageTabActive,
    settingsActive,
    missingPages: missing,
    layout,
    chats,
    access,
    onLockedClick,
  };
}
